import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/lib/components/Dialog";
import { Button } from "@/lib/components/Button/Button";
import { Input } from "@/lib/components/Input";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/lib/components/Form";
import { useParams } from "react-router";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { OverlayTemplateContentDto } from "@repo/api/overlays";
import { EditorApi } from "@/lib/api/editor-api";
import Toast from "@/lib/components/Toast";

const FormSchema = z.object({
  name: z.string().min(1, "Name is required"),
});

type FormValues = z.infer<typeof FormSchema>;

type Props = {
  onCancel: () => void;
  onClose: () => void;
};

export default function AddOrEditOverlayTemplate({ onCancel, onClose }: Props) {
  const { teamExternalId } = useParams();

  const composition = useEditorStore((state) => state.video!.compositionJson);

  const contentData: OverlayTemplateContentDto = {
    elements: composition.elements ?? {},
    startScene: composition.startScene ?? {},
    endScene: composition.endScene ?? {},
  };

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: "",
    },
  });

  const { isSubmitting } = form.formState;

  async function onSubmit(data: FormValues) {
    try {
      await EditorApi.createOverlayTemplate(teamExternalId!, {
        name: data.name,
        content: contentData,
      });

      Toast.info("Template created successfully");
      onClose();
    } catch (error) {
      console.error(error);
      Toast.error("Failed to create template");
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>New template</DialogTitle>
        <DialogDescription>
          Save current elements and scenes as a template.
        </DialogDescription>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Template name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <DialogFooter className="mt-8">
            <Button
              onClick={form.handleSubmit(onSubmit)}
              disabled={isSubmitting}
              isLoading={isSubmitting}
            >
              Save template
            </Button>
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
}
